import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import apiInstance from "../../apiInstance";
import { useSelector } from "react-redux";
import defaultDocImg from "../../../assets/images/doc.png";

const isImage = (mimetype) => mimetype && mimetype.startsWith("image/");

const AttachmentList = ({ attachments }) => {
    if (!attachments || attachments.length === 0) return null;
    return (
        <div className="mjs-attachments">
            {attachments.map((att) => {
                const url = `${process.env.REACT_APP_SERVER_URL}/uploads/support-tickets/${att.filename}`;
                return (
                    <div key={att.filename} className="mjs-attachment-preview">
                        {isImage(att.mimetype) ? (
                            <a href={url} target="_blank" rel="noreferrer">
                                <img
                                    src={url}
                                    alt={att.originalName}
                                />
                                <span>{att.originalName}</span>
                            </a>
                        ) : (
                            <a href={url} target="_blank" rel="noreferrer">
                                <img
                                    src={defaultDocImg}
                                    alt={att.originalName}
                                />
                                <span>{att.originalName}</span>
                            </a>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const DetailsPage = () => {

    const { ticketId } = useParams();
    const currUser = useSelector((state) => state.currentUser);
    const [ticketDetails, setTicketDetails] = useState({});
    const [reply, setReply] = useState("");
    const [replyFiles, setReplyFiles] = useState([]);
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        const selected = Array.from(e.target.files);
        if (replyFiles.length + selected.length > 5) {
            setError("Maximum 5 attachments allowed per reply.");
            return;
        }
        setReplyFiles((prev) => [...prev, ...selected]);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const removeReplyFile = (index) => {
        setReplyFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = () => {

        const messages = [
            ...(ticketDetails.messages || []),
            {
                sender: currUser.id,
                message: reply
            }
        ];

        const formData = new FormData();
        formData.append("type", "CUSTOMER_REPLIED");
        formData.append("messages", JSON.stringify(messages));

        replyFiles.forEach((file) => {
            formData.append("attachments", file);
        });

        apiInstance.put(`/dashboard/support-ticket/${ticketDetails._id}`, formData)
            .then((response) => {
                const returnedTicket = response.data.ticket;
                setTicketDetails(returnedTicket)
                setReply("")
                setReplyFiles([])
            })
            .catch((error) => {
                console.error("Unexpected error:", error);
                if (error.response) {
                    setError(error.response.data?.message || 'Something went wrong, please try again later');
                }
            });
    };

    useEffect(() => {
        const fetchTicketDetails = async () => {
            apiInstance
                .get(`/dashboard/support-ticket/${ticketId}`)
                .then((response) => {
                    setTicketDetails(response.data.ticket || {});
                })
                .catch((error) => {
                    console.error("Error fetching data:", error);
                    setError("Failed to load ticket details.");
                });
        };
        fetchTicketDetails();
    }, [ticketId]);

    return (
        <>
            <div className="mjs-dash-header">
                <button
                    className="mjs-btn"
                    onClick={() => navigate("/support")}
                >
                    Back
                </button>
                <div
                    className="mjs-badge"
                    style={{
                        backgroundColor:
                            ticketDetails.status === "open"
                                ? "#1565c0"
                                : ticketDetails.status === "in_progress"
                                ? "#f9a825"
                                : ticketDetails.status === "resolved"
                                ? "#2e7d32"
                                : "#c62828",
                    }}
                >
                    {ticketDetails.status?.replace("_", " ")}
                </div>

            </div>

            <div className="mjs-error">{error}</div>

            <div className="mjs-dash-section">
                <div className="mjs-dash-sec-header">
                    <h4>{ticketDetails.subject} [#{ticketDetails.ticket_number || ''}]</h4>
                </div>
                <div className="mjs-dash-inner-container">
                    <h5>Message</h5>
                    <div className="mjs-dash-row">
                        <span>{ticketDetails.description || "No description provided."}</span>
                    </div>
                    <AttachmentList
                        attachments={ticketDetails.attachments}
                    />
                </div>
                <div className="mjs-dash-inner-container">
                    <h5>Conversation</h5>
                    {ticketDetails.messages?.length > 0 ? (
                        ticketDetails.messages.map((item, idx) => (
                            <div className="mjs-dash-box" key={idx}>
                                <span className="mjs-strong">
                                    {item.sender?.name || "unknown"} {", "}
                                    {new Date(
                                        item.createdAt
                                    ).toLocaleDateString("en-GB", {
                                        day: "2-digit",
                                        month: "long",
                                        year: "numeric",
                                    })}
                                </span>
                                <span>{item.message}</span>
                                <AttachmentList
                                    attachments={item.attachments}
                                    ticketId={ticketDetails._id}
                                />
                            </div>
                        ))
                    ) : (
                        <>No replies yet.</>
                    )}
                </div>
                <div className="mjs-dash-inner-container">
                    <div className="mjs-dash-row" style={{ border: "none" }}>
                        <textarea
                            className="mjs-input"
                            name="reply"
                            value={reply}
                            onChange={(e) => setReply(e.target.value)}
                            style={{ width: "100%", height: "300px" }}
                        >
                            {reply}
                        </textarea>
                    </div>
                    <div className="mjs-dash-row" style={{ border: "none" }}>
                        <div className="mjs-input-field" style={{ margin: 0 }}>
                            <input
                                type="file"
                                multiple
                                ref={fileInputRef}
                                accept="image/jpeg,image/png,image/gif,image/webp,.pdf,.doc,.docx"
                                onChange={handleFileChange}
                            />
                            {replyFiles.length > 0 && (
                                <div className="mjs-attachment-list">
                                    {replyFiles.map((file, i) => (
                                        <div key={i} className="mjs-attachment-item">
                                            {file.type?.startsWith("image/") ? (
                                                <img
                                                    src={URL.createObjectURL(file)}
                                                    alt={file.name}
                                                    className="mjs-attachment-thumb"
                                                />
                                            ) : (
                                                <img
                                                    src={defaultDocImg}
                                                    alt={file.name}
                                                    className="mjs-attachment-thumb"
                                                />
                                            )}
                                            <span>{file.name}</span>
                                            <button type="button" onClick={() => removeReplyFile(i)}>&times;</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="mjs-dash-row">
                        <span></span>
                        <button className="mjs-btn" onClick={handleSubmit}>
                            Reply
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default DetailsPage;
